import React from 'react';
import CenteredPreviewOverlay from 'pages/components/CenteredPreviewOverlay';
import StaffReportComposerPreviewBody from 'pages/Reporting/StaffReportComposerPreviewBody';
import type { ReportDescriptionBlock } from 'pages/Reporting/reportDescriptionBlocks';
import type { ReportActivitySet } from 'pages/Reporting/reportActivitySets';
import type { Log } from '../hooks';
import type { ReportContextUser } from 'pages/Reporting/reportContextUsers';
import { isReportContextStandingLogType } from 'pages/Reporting/reportContextUsers';
import {
  filedReportPreviewBody,
  type FiledReportPreviewSnapshot,
} from '../filedReportPreviewSets';
import * as S from './LogTable.styled';

/** True when this activity row is a filed report with a full-screen set preview. */
export const isReportFiledLogType = (type: unknown): boolean => (
  String(type || '') === 'Report filed'
);

export const reportFiledPreviewFromLog = (log: Log): {
  subject: string,
  descBlocks: ReportDescriptionBlock[],
  activitySets: ReportActivitySet[],
  contextUsers: ReportContextUser[],
  alertLevelSummary: string,
} | null => {
  const raw = (log as Log & {
    reportFiledPreview?: FiledReportPreviewSnapshot | null,
  }).reportFiledPreview;
  return filedReportPreviewBody(raw);
};

/**
 * Report filed always; Compliance standing rows only when the filed snapshot is on the log.
 */
export const canPreviewFiledReportLog = (log: Log | null | undefined): boolean => {
  if (!log?.id) return false;
  if (isReportFiledLogType(log.type)) return true;
  if (!isReportContextStandingLogType(log.type)) return false;
  return Boolean(reportFiledPreviewFromLog(log));
};

/**
 * Full-screen filed report set — same chrome as composer / receiver preview.
 */
const FiledReportLogPreview = ({
  log,
  currency,
  onClose,
}: {
  log: Log,
  currency: string,
  onClose: () => void,
}) => {
  const preview = reportFiledPreviewFromLog(log);
  const subject = preview?.subject || 'Filed report';

  return (
    <CenteredPreviewOverlay
      subject={subject}
      previewKind="Filed report"
      ariaLabel="Filed report preview"
      draftSubtitle="Saved with Activity logs"
      onClose={onClose}
    >
      {preview ? (
        <StaffReportComposerPreviewBody
          subject={preview.subject}
          descBlocks={preview.descBlocks}
          activitySets={preview.activitySets}
          currency={currency}
          contextUsers={preview.contextUsers}
          alertLevelSummary={preview.alertLevelSummary}
        />
      ) : (
        <S.FiledReportPreviewEmpty>
          No report preview was saved with this log.
        </S.FiledReportPreviewEmpty>
      )}
    </CenteredPreviewOverlay>
  );
};

export default FiledReportLogPreview;
